/* eslint-disable import/no-cycle */
import { createSlice } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import {
	createReport,
	getEquipmentCalibrations,
	getWaveFiles,
	getOQReports
} from './report.generator.thunk';
import { REPORT_TYPES } from './report.generator.constants';

const initialState = {
	show: false,
	reportType: 'ON/OFF',
	sheetsIncluded: REPORT_TYPES.filter(report => report.default).map(
		report => report.name
	),
	extraExceptions: [],
	isACOnOff: false,
	files: [],
	isBusy: false,
	results: undefined,
	includePotentialGraph: false,
	potentialYLimit: -2100,
	potentialReferenceLines: [-850],
	includeWaveGraph: false,
	waveFiles: undefined,
	waveYLimit: -2700,
	waveReferenceLines: [-850, -1250],
	includeOQReports: false,
	oqReports: undefined,
	includeEquipmentCalibrations: false,
	equipmentCalibrations: undefined
};

const reportGeneratorSlice = createSlice({
	name: 'reportGenerator',
	initialState,
	reducers: {
		setShow(state, action) {
			state.show = action.payload;
		},
		pushSheetsIncluded(state, action) {
			state.sheetsIncluded = [...state.sheetsIncluded, action.payload];
		},
		popSheetsIncluded(state, action) {
			const newSheetsIncluded = [...state.sheetsIncluded];
			newSheetsIncluded.splice(newSheetsIncluded.indexOf(action.payload), 1);
			state.sheetsIncluded = newSheetsIncluded;
		},
		pushExtraExceptions(state, action) {
			state.extraExceptions = [...state.extraExceptions, action.payload];
		},
		popExtraExceptions(state, action) {
			const newExtraExceptions = [...state.extraExceptions];
			const index = newExtraExceptions.findIndex(
				e => e.guid === action.payload.guid
			);
			newExtraExceptions.splice(index, 1);
			state.extraExceptions = newExtraExceptions;
		},
		updateExtraException(state, action) {
			const newExtraExceptions = [...state.extraExceptions];
			const index = newExtraExceptions.findIndex(
				e => e.guid === action.payload.guid
			);
			newExtraExceptions.splice(index, 1, {
				...action.payload
				// guid: goodEnoughUUID()
			});
			state.extraExceptions = newExtraExceptions;
		},
		pushFiles(state, action) {
			state.files = [...state.files, action.payload];
		},
		popFiles(state, action) {
			const newFiles = [...state.files];
			newFiles.splice(newFiles.indexOf(action.payload), 1);
			state.files = newFiles;
		},
		setReportType(state, action) {
			state.reportType = action.payload;
		},
		setIsACOnOff(state, action) {
			state.isACOnOff = action.payload;
		},
		switchIncludePotentialGraph(state) {
			state.includePotentialGraph = !state.includePotentialGraph;
		},
		setPotentialYLimit(state, action) {
			state.potentialYLimit = action.payload;
		},
		setPotentialReferenceLines(state, action) {
			state.potentialReferenceLines = action.payload;
		},
		switchIncludeWaveGraph(state) {
			state.includeWaveGraph = !state.includeWaveGraph;
		},
		setWaveYLimit(state, action) {
			state.waveYLimit = action.payload;
		},
		setPotentialReferenceLines(state, action) {
			state.waveReferenceLines = action.payload;
		},
		switchIncludeOQReports(state) {
			state.includeOQReports = !state.includeOQReports;
		},
		switchIncludeEquipmentCalibrations(state) {
			state.includeEquipmentCalibrations = !state.includeEquipmentCalibrations;
		}
	},
	extraReducers: builder => {
		builder.addCase(createReport.pending, state => {
			state.isBusy = true;
		});
	},
	extraReducers: builder => {
		builder.addCase(createReport.pending, state => {
			state.isBusy = true;
			state.results = undefined;
		});
		builder.addCase(createReport.fulfilled, (state, action) => {
			state.isBusy = false;
			const { payload } = action;
			state.results = payload.output;
		});
		builder.addCase(createReport.rejected, state => {
			state.isBusy = false;
		});
		builder.addCase(getWaveFiles.fulfilled, (state, action) => {
			state.waveFiles = action.payload;
		});
		builder.addCase(getOQReports.fulfilled, (state, action) => {
			state.oqReports = action.payload;
		});
		builder.addCase(getEquipmentCalibrations.fulfilled, (state, action) => {
			state.equipmentCalibrations = action.payload;
		});
	},
	selectors: {
		selectShow: sliceState => sliceState.show,
		selectSheetsIncluded: sliceState => sliceState.sheetsIncluded,
		selectExtraExceptions: sliceState => sliceState.extraExceptions,
		selectReportType: sliceState => sliceState.reportType,
		selectIsACOnOff: sliceState => sliceState.isACOnOff,
		selectFiles: sliceState => sliceState.files,
		selectIsBusy: sliceState => sliceState.isBusy,
		selectResults: sliceState => sliceState.results,
		selectIncludePotentialGraph: sliceState => sliceState.includePotentialGraph,
		selectPotentialYLimit: sliceState => sliceState.potentialYLimit,
		selectPotentialReferenceLines: sliceState =>
			sliceState.potentialReferenceLines,
		selectIncludeWaveGraph: sliceState => sliceState.includeWaveGraph,
		selectWaveFiles: sliceState => sliceState.waveFiles,
		selectWaveYLimit: sliceState => sliceState.waveYLimit,
		selectWaveReferenceLines: sliceState => sliceState.waveReferenceLines,
		selectIncludeOQReports: sliceState => sliceState.includeOQReports,
		selectOQReports: sliceState => sliceState.oqReports,
		selectIncludeEquipmentCalibrations: sliceState =>
			sliceState.includeEquipmentCalibrations,
		selectEquipmentCalibration: sliceState => sliceState.equipmentCalibrations
	}
});

const selectState = state => state.cisview;

export const selectShow = createSelector(
	selectState,
	reportGeneratorSlice.selectors.selectShow
);

export const selectSheetsIncluded = createSelector(
	selectState,
	reportGeneratorSlice.selectors.selectSheetsIncluded
);
export const selectExtraExceptions = createSelector(
	selectState,
	reportGeneratorSlice.selectors.selectExtraExceptions
);

export const selectReportType = createSelector(
	selectState,
	reportGeneratorSlice.selectors.selectReportType
);

export const selectIsACOnOff = createSelector(
	selectState,
	reportGeneratorSlice.selectors.selectIsACOnOff
);

export const selectFiles = createSelector(
	selectState,
	reportGeneratorSlice.selectors.selectFiles
);

export const selectIsBusy = createSelector(
	selectState,
	reportGeneratorSlice.selectors.selectIsBusy
);

export const selectResults = createSelector(
	selectState,
	reportGeneratorSlice.selectors.selectResults
);

export const selectIncludePotentialGraph = createSelector(
	selectState,
	reportGeneratorSlice.selectors.selectIncludePotentialGraph
);

export const selectPotentialYLimit = createSelector(
	selectState,
	reportGeneratorSlice.selectors.selectPotentialYLimit
);

export const selectPotentialReferenceLines = createSelector(
	selectState,
	reportGeneratorSlice.selectors.selectPotentialReferenceLines
);

export const selectIncludeWaveGraph = createSelector(
	selectState,
	reportGeneratorSlice.selectors.selectIncludeWaveGraph
);

export const selectWaveFiles = createSelector(
	selectState,
	reportGeneratorSlice.selectors.selectWaveFiles
);

export const selectWaveYLimit = createSelector(
	selectState,
	reportGeneratorSlice.selectors.selectWaveYLimit
);

export const selectWaveReferenceLines = createSelector(
	selectState,
	reportGeneratorSlice.selectors.selectWaveReferenceLines
);
export const selectIncludeOQReports = createSelector(
	selectState,
	reportGeneratorSlice.selectors.selectIncludeOQReports
);
export const selectOQReports = createSelector(
	selectState,
	reportGeneratorSlice.selectors.selectOQReports
);

export const selectIncludeEquipmentCalibrations = createSelector(
	selectState,
	reportGeneratorSlice.selectors.selectIncludeEquipmentCalibrations
);
export const selectEquipmentCalibration = createSelector(
	selectState,
	reportGeneratorSlice.selectors.selectEquipmentCalibration
);

export const {
	setShow,
	pushSheetsIncluded,
	popSheetsIncluded,
	pushExtraExceptions,
	popExtraExceptions,
	setReportType,
	setIsACOnOff,
	pushFiles,
	popFiles,
	updateExtraException,
	switchIncludePotentialGraph,
	setPotentialYLimit,
	setPotentialReferenceLines,
	switchIncludeWaveGraph,
	setWaveYLimit,
	setWaveReferenceLines,
	switchIncludeOQReports,
	switchIncludeEquipmentCalibrations
} = reportGeneratorSlice.actions;

export default reportGeneratorSlice.reducer;
